import { Outlet } from 'react-router-dom';
import { ArrowLeft, Settings } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './Button';
import Input from './Input';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleAdminAccess = () => {
    setShowPasswordModal(true);
    setPassword('');
    setPasswordError('');
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'horse2015') {
      setShowPasswordModal(false);
      navigate('/admin');
    } else {
      setPasswordError('Incorrect password');
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Fixed position top banner */}
      <header className="fixed top-0 left-0 w-full z-50 bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center">
            {!isHomePage && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-full hover:bg-gray-700 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft size={20} />
              </motion.button>
            )}
            <h1 
              onClick={() => navigate('/')}
              className="text-xl md:text-2xl font-heading font-bold cursor-pointer"
            >
              Salina Independence Day Rodeo
            </h1>
          </div>
        </div>
      </header>
      
      {/* Increased padding top from pt-44 to pt-24 for better spacing */}
      <main className="container mx-auto px-4 py-6 md:py-8 pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <Outlet />
        </motion.div>
      </main>
      
      <footer className="bg-gradient-to-b from-gray-800 to-gray-900 text-white py-6 relative">
        <div className="container mx-auto px-4">
          <div className="text-center text-sm">
            <p>&copy; {new Date().getFullYear()} Salina Independence Day Rodeo. All rights reserved.</p>
          </div>
          
          {/* Settings gear icon */}
          <button
            onClick={handleAdminAccess}
            className="absolute bottom-4 right-4 p-2 rounded-full hover:bg-gray-700 transition-colors opacity-50 hover:opacity-100"
            aria-label="Admin settings"
          >
            <Settings size={20} className="text-white" />
          </button>
        </div>
      </footer>

      {/* Password Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => setShowPasswordModal(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">
                Admin Access
              </h2>
              
              <form onSubmit={handlePasswordSubmit}>
                <Input
                  label="Enter Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={passwordError}
                  placeholder="Enter admin password"
                  autoFocus
                />
                
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPasswordModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary">
                    Access Admin
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;